/**
 * Avatar / Profile Backgrounds
 *
 * A decorative backdrop that renders behind the avatar (and on the profile).
 * Earned-only — acquired via challenges and surprise-box drops, never bought.
 *
 * Ships as an EMPTY catalog until real backdrop assets land (per the
 * no-fabrication rule — see CHALLENGES_SYSTEM.md §5.1). The data model, equip
 * slot, render path, and box/challenge grantability are all in place, so adding
 * an entry below (plus its CSS class in backgrounds.css) makes it immediately
 * ownable, equippable, and droppable — no further code.
 */

import type { Rarity, Acquisition } from './rarity';

export interface Background {
  id: string;
  name: string;
  description: string;
  /** Box dust/refund value — backgrounds are earned-only, never purchased. */
  price: number;
  rarity: Rarity;
  /** Always 'earned' for backgrounds (challenge rewards / box drops). */
  acquisition?: Acquisition;
  /** CSS class applied on the avatar wrapper to paint the backdrop. */
  cssClass: string;
  /** Accent color used for swatches / fallback rendering. */
  swatchColor: string;
}

// Empty until real assets land. Boxes/challenges only draw from real entries,
// so nothing fabricated can be granted.
export const BACKGROUNDS: Background[] = [];

export function getBackground(id: string | undefined): Background | null {
  if (!id) return null;
  return BACKGROUNDS.find(b => b.id === id) ?? null;
}
